import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import OneSignalInit from "@/components/OneSignalInit";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Search from "@/pages/Search";
import CollectorProfile from "@/pages/CollectorProfile";
import CollectorDashboard from "@/pages/CollectorDashboard";
import CustomerDashboard from "@/pages/CustomerDashboard";
import Subscribe from "@/pages/Subscribe";
import PaymentSuccess from "@/pages/PaymentSuccess";
import MyRequest from "@/pages/MyRequest";
import ManageSubscription from "@/pages/ManageSubscription";
import LegalPage from "@/pages/LegalPage";
import AccountRecovery from "@/pages/AccountRecovery";
import ResetPassword from "@/pages/ResetPassword";
import AdminConsentLog from "@/pages/AdminConsentLog";
import AdminHome from "@/pages/AdminHome";
import AdminCollectors from "@/pages/AdminCollectors";
import AdminCollectorDetail from "@/pages/AdminCollectorDetail";
import AdminCustomers from "@/pages/AdminCustomers";
import AdminCustomerDetail from "@/pages/AdminCustomerDetail";
import AdminAds from "@/pages/AdminAds";
import AdminAdForm from "@/pages/AdminAdForm";
import AdminPayments from "@/pages/AdminPayments";
import AdminLeadInsights from "@/pages/AdminLeadInsights";
import AdminUnmatched from "@/pages/AdminUnmatched";
import AdminReports from "@/pages/AdminReports";
import AdminAuditLogs from "@/pages/AdminAuditLogs";
import AdminMarketing from "@/pages/AdminMarketing";
import AdminReferrals from "@/pages/AdminReferrals";
import AdminPromos from "@/pages/AdminPromos";
import AdminMonitoring from "@/pages/AdminMonitoring";
import AdminSuggestions from "@/pages/AdminSuggestions";
import AdminReviews from "@/pages/AdminReviews";
import AdminManageAdmins from "@/pages/AdminManageAdmins";
import AdminAccount from "@/pages/AdminAccount";
import Suggestions from "@/pages/Suggestions";
import BillingHistoryPage from "@/pages/BillingHistoryPage";
import LeadInsightsPage from "@/pages/LeadInsightsPage";
import CompanyDashboard from "@/pages/CompanyDashboard";
import CompanyCollector from "@/pages/CompanyCollector";
import InviteAccept from "@/pages/InviteAccept";
import DashboardRedirect from "@/pages/DashboardRedirect";
import AdminCompanies from "@/pages/AdminCompanies";
import AdminCompanyDetail from "@/pages/AdminCompanyDetail";
import InactivityLogout from "@/components/InactivityLogout";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <OneSignalInit />
          <InactivityLogout />
          <Header />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/recover" element={<AccountRecovery />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/search" element={<Search />} />
            <Route path="/suggestions" element={<Suggestions />} />
            <Route path="/my-request/:token" element={<MyRequest />} />
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route path="/collectors/:id" element={<CollectorProfile />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/dashboard" element={<DashboardRedirect />} />
            <Route path="/dashboard/company" element={
              <ProtectedRoute role="company"><CompanyDashboard /></ProtectedRoute>
            } />
            <Route path="/dashboard/company/collectors/:id" element={
              <ProtectedRoute role="company"><CompanyCollector /></ProtectedRoute>
            } />
            <Route path="/subscribe" element={
              <ProtectedRoute role="collector"><Subscribe /></ProtectedRoute>
            } />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/dashboard/collector" element={
              <ProtectedRoute role="collector"><CollectorDashboard /></ProtectedRoute>
            } />
            <Route path="/dashboard/customer" element={
              <ProtectedRoute role="customer"><CustomerDashboard /></ProtectedRoute>
            } />
            <Route path="/dashboard/subscription" element={
              <ProtectedRoute role="collector"><ManageSubscription /></ProtectedRoute>
            } />
            <Route path="/dashboard/billing" element={
              <ProtectedRoute role="collector"><BillingHistoryPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/leads/insights" element={
              <ProtectedRoute role="collector"><LeadInsightsPage /></ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute role="admin"><AdminHome /></ProtectedRoute>
            } />
            <Route path="/admin/collectors" element={
              <ProtectedRoute role="admin" feature="collectors"><AdminCollectors /></ProtectedRoute>
            } />
            <Route path="/admin/collectors/:id" element={
              <ProtectedRoute role="admin" feature="collectors"><AdminCollectorDetail /></ProtectedRoute>
            } />
            <Route path="/admin/companies" element={
              <ProtectedRoute role="admin" feature="collectors"><AdminCompanies /></ProtectedRoute>
            } />
            <Route path="/admin/companies/:id" element={
              <ProtectedRoute role="admin" feature="collectors"><AdminCompanyDetail /></ProtectedRoute>
            } />
            <Route path="/admin/customers" element={
              <ProtectedRoute role="admin" feature="customers"><AdminCustomers /></ProtectedRoute>
            } />
            <Route path="/admin/customers/:id" element={
              <ProtectedRoute role="admin" feature="customers"><AdminCustomerDetail /></ProtectedRoute>
            } />
            <Route path="/admin/marketing" element={
              <ProtectedRoute role="admin" feature="marketing"><AdminMarketing /></ProtectedRoute>
            } />
            <Route path="/admin/referrals" element={
              <ProtectedRoute role="admin" feature="referrals"><AdminReferrals /></ProtectedRoute>
            } />
            <Route path="/admin/promos" element={
              <ProtectedRoute role="admin" feature="promos"><AdminPromos /></ProtectedRoute>
            } />
            <Route path="/admin/monitoring" element={
              <ProtectedRoute role="admin" feature="monitoring"><AdminMonitoring /></ProtectedRoute>
            } />
            <Route path="/admin/suggestions" element={
              <ProtectedRoute role="admin" feature="suggestions"><AdminSuggestions /></ProtectedRoute>
            } />
            <Route path="/admin/ads" element={
              <ProtectedRoute role="admin" feature="ads"><AdminAds /></ProtectedRoute>
            } />
            <Route path="/admin/ads/new" element={
              <ProtectedRoute role="admin" feature="ads"><AdminAdForm /></ProtectedRoute>
            } />
            <Route path="/admin/ads/:id" element={
              <ProtectedRoute role="admin" feature="ads"><AdminAdForm /></ProtectedRoute>
            } />
            <Route path="/admin/payments" element={
              <ProtectedRoute role="admin" feature="payments"><AdminPayments /></ProtectedRoute>
            } />
            <Route path="/admin/leads" element={
              <ProtectedRoute role="admin" feature="leads"><AdminLeadInsights /></ProtectedRoute>
            } />
            <Route path="/admin/unmatched" element={
              <ProtectedRoute role="admin" feature="leads"><AdminUnmatched /></ProtectedRoute>
            } />
            <Route path="/admin/reports" element={
              <ProtectedRoute role="admin" feature="reports"><AdminReports /></ProtectedRoute>
            } />
            <Route path="/admin/audit" element={
              <ProtectedRoute role="admin" feature="audit"><AdminAuditLogs /></ProtectedRoute>
            } />
            <Route path="/admin/reviews" element={
              <ProtectedRoute role="admin" feature="reviews"><AdminReviews /></ProtectedRoute>
            } />
            <Route path="/admin/admins" element={
              <ProtectedRoute role="admin" superOnly><AdminManageAdmins /></ProtectedRoute>
            } />
            <Route path="/admin/account" element={
              <ProtectedRoute role="admin"><AdminAccount /></ProtectedRoute>
            } />
          </Routes>
          <Footer />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
